import { StrictMode } from "react";
import { createRoot, hydrateRoot, type Root } from "react-dom/client";
import { QueryClientProvider, type DehydratedState, HydrationBoundary } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { App } from "./App";
import { queryClient } from "./lib/queryClient.ts";
import { bootRouter } from "./lib/router.ts";

declare global {
  interface Window {
    __QUERY_STATE__?: DehydratedState;
  }
}

const IS_DEV = process.env.NODE_ENV !== "production";
type HmrData = {
  root?: Root;
  recoverBunHmrRuntimeError?: (event: ErrorEvent) => void;
};
const hmrData = import.meta.hot.data as HmrData;

if (IS_DEV && !hmrData.recoverBunHmrRuntimeError) {
  hmrData.recoverBunHmrRuntimeError = (event) => {
    const message = event.error instanceof Error ? event.error.message : String(event.message ?? "");
    if (message.includes("Unknown HMR script")) {
      event.preventDefault();
      window.location.reload();
    }
  };
  window.addEventListener("error", hmrData.recoverBunHmrRuntimeError, true);
}

// Favicon / PWA 링크 런타임 주입. HTML 에 두면 Bun 번들러가 절대경로를
// 자산으로 resolve 시도해 실패 → 서버 고정 라우트(/favicon.ico 등) 가리키게 JS 로 추가.
function injectHeadLinks() {
  const links: Array<[string, Record<string, string>]> = [
    ['link', { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' }],
    ['link', { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' }],
    ['link', { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' }],
    ['link', { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' }],
    ['link', { rel: 'manifest', href: '/site.webmanifest' }],
  ];
  for (const [tag, attrs] of links) {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
}
injectHeadLinks();
bootRouter();

const elem = document.getElementById("root")!;
const dehydrated = window.__QUERY_STATE__;
// One-shot: drop after read so HMR remounts don't re-apply a stale snapshot.
delete window.__QUERY_STATE__;

const app = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydrated}>
        <App />
      </HydrationBoundary>
      {IS_DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>
);

// Prerendered pages ship HTML in #root — hydrate to keep LCP image / layout.
// Empty shell (dev, SPA fallback) uses createRoot as before.
if (elem.hasChildNodes()) {
  hydrateRoot(elem, app);
} else {
  const root = (hmrData.root ??= createRoot(elem));
  root.render(app);
}
import.meta.hot.accept();
